import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ArrowRight, Menu, X } from "lucide-react";
import { useI18n } from "../i18n";
import { cn } from "../lib/utils";
import { LogoFull } from "./logo";
import { LangFlags } from "./lang-flags";
import { Button } from "./ui/button";

const LINKS = [
  { href: "#produtos", key: "nav.central" },
  { href: "#servicos", key: "nav.servicos" },
  { href: "#processo", key: "nav.processo" },
  { href: "#seguranca", key: "nav.seguranca" },
  { href: "#faq", key: "nav.faq" },
];

/** Barra fixa de largura total — papel sólido com hairline embaixo ao rolar. */
export function Navbar() {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // trava o scroll do fundo enquanto o menu mobile está aberto
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Esc fecha o menu
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-50 border-b transition-all duration-300",
          scrolled || open
            ? "border-line bg-background/92 backdrop-blur-md"
            : "border-transparent bg-transparent",
        )}
      >
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
          <a href="#topo" aria-label="Vorto" className="shrink-0 py-2">
            <LogoFull className="h-6 w-auto" />
          </a>

          <nav className="hidden items-center gap-0.5 lg:flex" aria-label="Principal">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="rounded-md px-3.5 py-2 text-[13.5px] font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.05] hover:text-foreground"
              >
                {t(l.key)}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <LangFlags className="hidden sm:flex" />
            <Button href="#contato" size="sm" className="hidden md:inline-flex">
              {t("nav.cta")}
              <ArrowRight />
            </Button>
            <button
              type="button"
              onClick={() => setOpen(!open)}
              aria-expanded={open}
              aria-label={open ? t("nav.fechar") : t("nav.menu")}
              className="grid size-10 place-items-center rounded-md border border-line bg-surface text-foreground transition-colors hover:border-[#c9c5b6] lg:hidden"
            >
              {open ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Menu mobile: folha de papel cobrindo a tela */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 flex flex-col bg-background px-6 pt-24 pb-10 lg:hidden"
          >
            <nav className="flex flex-1 flex-col justify-center" aria-label="Principal (mobile)">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="group flex items-baseline justify-between gap-4 border-b border-line py-4"
                >
                  <span className="flex items-baseline gap-4">
                    <span className="font-mono text-xs text-subtle tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display text-2xl font-medium tracking-tight text-foreground transition-colors group-hover:text-accent">
                      {t(l.key)}
                    </span>
                  </span>
                  <ArrowRight className="size-5 -translate-x-2 self-center text-subtle opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:text-accent group-hover:opacity-100" />
                </motion.a>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28, duration: 0.35 }}
              className="flex flex-col gap-4"
            >
              <Button href="#contato" size="lg" onClick={() => setOpen(false)}>
                {t("nav.cta")}
                <ArrowRight />
              </Button>
              <LangFlags className="self-center" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
